import { ChangeDetectionStrategy, Component, computed, inject, input, signal, viewChild } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { map } from 'rxjs';
import { DeliveryPlanningComponent } from './delivery-planning/delivery-planning.component';
import { DrillPlanningComponent } from './drill-planning/drill-planning.component';
import { DrillColorBy, PeriodBounds, PlanningMode, TimeScale } from './planning.types';
import { PlanningControlsComponent } from './ui/planning-controls/planning-controls.component';
import { MonthOption, buildMonthOptions, computeBounds, monthIdToDate, shiftAnchor, toMonthId } from './utils/column-generator';

/**
 * Onglet Planning : détient la barre de contrôles commune aux sous-onglets
 * forage et livraison, pour le site sélectionné dans l'arbre de gauche.
 */
@Component({
  selector: 'app-planning',
  standalone: true,
  templateUrl: './planning.component.html',
  styleUrls: ['./planning.component.scss'],
  imports: [TranslateModule, PlanningControlsComponent, DrillPlanningComponent, DeliveryPlanningComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PlanningComponent {
  /** Site courant ; l'arbre le fournit sous forme de chaîne ou de nombre. */
  readonly siteId = input<string | number | null>(null);

  protected readonly mode      = signal<PlanningMode>('drilling');
  protected readonly colorBy   = signal<DrillColorBy>('rig');
  protected readonly timeScale = signal<TimeScale>('week');

  private readonly anchorDate = signal<Date>(new Date());

  protected readonly deliveryPlanning = viewChild(DeliveryPlanningComponent);

  /** Le rapport ne porte que sur des livraisons : il faut au moins un shift sélectionné qui en contienne. */
  protected readonly reportDisabled = computed<boolean>(() =>
    this.mode() !== 'delivery' || !this.deliveryPlanning()?.canReport(),
  );

  private readonly translate = inject(TranslateService);
  private readonly lang = toSignal(
    this.translate.onLangChange.pipe(map(event => event.lang)),
    { initialValue: this.translate.currentLang },
  );

  /** Identifiant exploitable par les services, ou null si rien n'est sélectionné. */
  protected readonly resolvedSiteId = computed<number | null>(() => {
    const raw = this.siteId();
    if (raw === null || raw === '') { return null; }
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
  });

  protected readonly bounds = computed<PeriodBounds>(() =>
    computeBounds(this.timeScale(), this.anchorDate()),
  );

  protected readonly monthOptions = computed<MonthOption[]>(() => buildMonthOptions(this.anchorDate(), this.lang() || undefined));
  protected readonly monthId      = computed<string>(() => toMonthId(this.anchorDate()));

  /** Le premier critère de couleur diffère selon le sous-onglet. */
  protected readonly primaryColorByLabel = computed<string>(() =>
    this.mode() === 'drilling' ? 'PLANNING.RIG' : 'PLANNING.EQUIPMENT',
  );

  protected setMode(mode: PlanningMode): void {
    this.mode.set(mode);
  }

  protected onColorByChange(value: DrillColorBy): void {
    this.colorBy.set(value);
  }

  protected onTimeScaleChange(scale: TimeScale): void {
    this.timeScale.set(scale);
  }

  protected onMonthChange(monthId: string): void {
    const firstOfMonth = monthIdToDate(monthId);
    if (firstOfMonth) { this.anchorDate.set(firstOfMonth); }
  }

  protected onToday(): void {
    this.anchorDate.set(new Date());
  }

  protected onNavigate(direction: -1 | 1): void {
    this.anchorDate.update(anchor => shiftAnchor(this.timeScale(), anchor, direction));
  }
}
